import React, { useEffect, useMemo, useState } from 'react';
import { GridActionsCellItem, GridRowModes, useGridApiRef } from '@mui/x-data-grid-premium';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { v4 as uuidv4 } from 'uuid';

import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import { dataGridPremiumColumns } from '../../../utils/helper';
import useConfig from '../../../context/ConfigContext';
import { DataGridIdentifiers } from '../../Interface/Datagrid/DataGridIdentifiers';
import {
  AutoCompleteSelectUI,
  ButtonUI,
  CheckboxUI,
  DatagridPremiumUi,
  FileUploadDialogUI,
  InputUI,
} from '../../Interface';

const DocumentTypeAttachmentTab = ({
  attachmentRows,
  setAttachmentRows,
  disabled,
  handleDelete,
  languageOptions = [],
}) => {
  const { config, getConfig } = useConfig();
  const apiRef = useGridApiRef();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  useEffect(() => {
    if (!Object.keys(config ?? {}).length) {
      getConfig();
    }
  }, [config, getConfig]);

  useEffect(() => {
    if (!disabled) return;

    const rowsInEditMode = Object.keys(apiRef.current?.state?.editRows ?? {});
    for (const rowId of rowsInEditMode) {
      apiRef.current.stopRowEditMode({ id: rowId, ignoreModifications: false });
    }
  }, [apiRef, disabled]);

  const defaultLangCode = config?.countryCode ?? null;

  const getLanguageLabel = (langCode) => {
    return languageOptions.find((item) => item.value === langCode)?.label ?? '';
  };

  const triggerDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDownload = async (row) => {
    if (row.file instanceof File) {
      const url = URL.createObjectURL(row.file);
      triggerDownload(url, row.name);
      URL.revokeObjectURL(url);
      return;
    }

    if (row.path) {
      const url = `${process.env.REACT_APP_API_URL}/${row.path}`;
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      triggerDownload(blobUrl, row.name);
      URL.revokeObjectURL(blobUrl);
    }
  };

  const handlePreview = (row) => {
    if (row.file instanceof File) {
      const url = URL.createObjectURL(row.file);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return;
    }

    if (row.path) {
      window.open(`${process.env.REACT_APP_API_URL}/${row.path}`, '_blank');
    }
  };

  const applyRowRules = (rows, changedRowId, changes) => {
    const nextRows = rows.map((row) => {
      if (row.id !== changedRowId) return row;

      const nextLangCode = changes.langCode !== undefined ? changes.langCode : row.langCode;
      const nextDocLangDefault = nextLangCode ? changes.docLangDefault ?? row.docLangDefault : false;

      return {
        ...row,
        ...changes,
        langCode: nextLangCode,
        docLangDefault: nextDocLangDefault,
      };
    });

    const changedRow = nextRows.find((row) => row.id === changedRowId);
    if (!changedRow) return nextRows;

    return nextRows.map((row) => {
      if (row.id === changedRowId) return row;

      const nextRow = { ...row };

      if (changedRow.docDefault) {
        nextRow.docDefault = false;
      }

      if (changedRow.docLangDefault && row.langCode === changedRow.langCode) {
        nextRow.docLangDefault = false;
      }

      return nextRow;
    });
  };

  const updateRow = (rowId, changes) => {
    setAttachmentRows((prev) => applyRowRules(prev, rowId, changes));
  };

  const handleAddFiles = (files) => {
    const existingNames = new Set(attachmentRows.map((row) => row.name));
    const duplicateName = files.find((file) => existingNames.has(file.name));

    if (duplicateName) {
      addNotification({
        content: `Már szerepel ilyen nevű melléklet a listában: ${duplicateName.name}`,
        type: NotificationType.ERROR,
      });
      return;
    }

    const newRows = files.map((file) => ({
      id: uuidv4(),
      file,
      label: '',
      name: file.name,
      extension: file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : '',
      langCode: defaultLangCode,
      docDefault: false,
      docLangDefault: false,
      isDraft: true,
    }));

    setAttachmentRows((prev) => [...prev, ...newRows]);
    setUploadDialogOpen(false);
  };

  const handleRemoveRow = (row) => {
    if (row.isDraft) {
      setAttachmentRows((prev) => prev.filter((item) => item.id !== row.id));
      return;
    }

    handleDelete(row);
  };

  const startEditRow = (rowId) => {
    const rowsInEditMode = Object.keys(apiRef.current?.state?.editRows ?? {});

    for (const currentRowId of rowsInEditMode) {
      if (rowId.toString() !== currentRowId) {
        apiRef.current.stopRowEditMode({ id: currentRowId, ignoreModifications: false });
      }
    }

    const rowMode = apiRef.current.getRowMode(rowId);
    if (rowMode === GridRowModes.View) {
      apiRef.current.startRowEditMode({ id: rowId });
    }
  };

  const columns = useMemo(
    () =>
      dataGridPremiumColumns([
        {
          field: 'label',
          headerName: 'Megnevezés',
          type: 'string',
          minWidth: 260,
          flex: 1,
          editable: true,
          sortable: false,
          renderCell: (params) => params.row.label ?? '',
          renderEditCell: (params) => (
            <div onClick={(event) => event.stopPropagation()} className="w-full">
              <InputUI
                variant="standard"
                value={params.row.label ?? ''}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  apiRef.current.setEditCellValue({
                    id: params.id,
                    field: params.field,
                    value: nextValue,
                  });
                  updateRow(params.id, { label: nextValue });
                }}
              />
            </div>
          ),
        },
        {
          field: 'name',
          headerName: 'Fájlnév',
          type: 'string',
          minWidth: 260,
          flex: 0.9,
          sortable: false,
        },
        {
          field: 'langCode',
          headerName: 'Nyelv',
          type: 'string',
          minWidth: 220,
          flex: 0.8,
          editable: true,
          sortable: false,
          renderCell: (params) => {
            const language = params.row.language;
            if (language?.name) {
              return [language.code, language.name].filter(Boolean).join(' - ');
            }
            return getLanguageLabel(params.row.langCode);
          },
          renderEditCell: (params) => (
            <div onClick={(event) => event.stopPropagation()} className="w-full">
              <AutoCompleteSelectUI
                id={`attachment-lang-${params.id}`}
                fullWidth
                dataset={languageOptions}
                selectedValue={params.row.langCode}
                selectedLabelValue={getLanguageLabel(params.row.langCode)}
                onChange={(_event, newVal) => {
                  const nextValue = newVal?.value ?? null;

                  apiRef.current.setEditCellValue({
                    id: params.id,
                    field: params.field,
                    value: nextValue,
                  });

                  updateRow(params.id, {
                    langCode: nextValue,
                    language: null,
                    ...(newVal ? {} : { docLangDefault: false }),
                  });
                }}
              />
            </div>
          ),
        },
        {
          field: 'docDefault',
          headerName: 'Alapértelmezett',
          type: 'boolean',
          minWidth: 150,
          flex: 0.45,
          editable: true,
          sortable: false,
          renderCell: (params) => <CheckboxUI checked={Boolean(params.row.docDefault)} disabled />,
          renderEditCell: (params) => (
            <div onClick={(event) => event.stopPropagation()}>
              <CheckboxUI
                checked={Boolean(params.row.docDefault)}
                onCheckedChange={(checked) => {
                  apiRef.current.setEditCellValue({
                    id: params.id,
                    field: params.field,
                    value: checked,
                  });
                  updateRow(params.id, { docDefault: checked });
                }}
              />
            </div>
          ),
        },
        {
          field: 'docLangDefault',
          headerName: 'Nyelvi alapért.',
          type: 'boolean',
          minWidth: 170,
          flex: 0.5,
          editable: true,
          sortable: false,
          renderCell: (params) => <CheckboxUI checked={Boolean(params.row.docLangDefault)} disabled />,
          renderEditCell: (params) => (
            <div onClick={(event) => event.stopPropagation()}>
              <CheckboxUI
                checked={Boolean(params.row.docLangDefault)}
                disabled={!params.row.langCode}
                onCheckedChange={(checked) => {
                  apiRef.current.setEditCellValue({
                    id: params.id,
                    field: params.field,
                    value: checked,
                  });
                  updateRow(params.id, { docLangDefault: checked });
                }}
              />
            </div>
          ),
        },
        {
          field: 'actions',
          type: 'actions',
          headerName: 'Műveletek',
          minWidth: 180,
          getActions: ({ row }) => {
            const previewable = ['.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'].includes(
              (row.extension || '').toLowerCase()
            );

            return [
              ...(previewable
                ? [
                    <GridActionsCellItem
                      icon={<VisibilityIcon />}
                      label="Preview"
                      onClick={() => handlePreview(row)}
                      color="inherit"
                    />,
                  ]
                : []),
              <GridActionsCellItem
                icon={<DownloadIcon />}
                label="Download"
                onClick={() => handleDownload(row)}
                color="inherit"
              />,
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={() => handleRemoveRow(row)}
                color="inherit"
              />,
            ];
          },
        },
      ]),
    [apiRef, attachmentRows, languageOptions]
  );

  return (
    <>
      <ButtonUI
        className="bg-success mb-1"
        text="Új fájl"
        type="button"
        onClick={() => setUploadDialogOpen(true)}
        disabled={disabled}
      />

      <div className="overflow-x-auto">
        <div className="align-middle">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <DatagridPremiumUi
              apiRef={apiRef}
              datagridIdentifier={DataGridIdentifiers.documentReports}
              height={420}
              columns={columns}
              rows={attachmentRows}
              editMode="row"
              headerFilters={false}
              disableSave
              onCellDoubleClick={(_params, event) => {
                event.defaultMuiPrevented = true;
              }}
              onCellClick={(params) => {
                if (disabled || params.field === 'actions') {
                  return;
                }

                startEditRow(params.row.id);
              }}
              onRowEditStop={() => {}}
              disableRowSelectionOnClick
              getRowHeight={() => 'auto'}
            />
          </div>
        </div>
      </div>

      <FileUploadDialogUI
        open={uploadDialogOpen}
        handleClose={() => setUploadDialogOpen(false)}
        handleUpload={handleAddFiles}
        disabled={disabled}
      />
    </>
  );
};

export default DocumentTypeAttachmentTab;
